import React, { useMemo } from "react";

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";

import { border, palette } from "../styles";
import { FilePickerSelectionReturn } from "../hooks/use-file-picker-selection";
import { TemplateSelectionReturn } from "../hooks/use-template-selection";

function truncateName(name: string, maxLen = 40): string {
  if (name.length <= maxLen) return name;
  return name.slice(0, 18) + "\u2026" + name.slice(-18);
}

interface TemplateSelectionPanelProps {
  mode: "template";
  selection: TemplateSelectionReturn;
}

interface FilePickerSelectionPanelProps {
  mode: "file";
  selection: FilePickerSelectionReturn;
}

type Props = TemplateSelectionPanelProps | FilePickerSelectionPanelProps;

export const SelectionPanel: React.FC<Props> = (props) => {
  const { mode, selection } = props;

  const templateChips = useMemo(() => {
    if (mode !== "template") return { folders: [], files: [], forms: [], documents: [] };
    const tmpl = selection as TemplateSelectionReturn;
    return {
      folders: tmpl.state.folders.map((f) => ({
        id: f.id,
        name: tmpl.folders.find((folder) => folder.id === f.id)?.title || `Folder ${f.id}`
      })),
      files: tmpl.state.files.map((f) => ({
        id: f.id,
        name: tmpl.files.find((file) => file.id === f.id)?.name || `File ${f.id}`
      })),
      forms: tmpl.state.forms.map((f) => ({
        id: f.id,
        name: tmpl.forms.find((form) => form.id === f.id)?.title || `Form ${f.id}`
      })),
      documents: tmpl.state.documents.map((d) => ({
        id: d.id,
        name: tmpl.documents.find((doc) => doc.id === d.id)?.title || `Document ${d.id}`
      }))
    };
  }, [mode, selection]);

  const fileChips = useMemo(() => {
    if (mode !== "file") return [];
    const ext = selection as FilePickerSelectionReturn;
    return ext.resolvedSelectedFiles.map((f) => ({ id: f.id, name: f.name }));
  }, [mode, selection]);

  if (mode === "template") {
    const tmpl = selection as TemplateSelectionReturn;
    const folderCount = tmpl.state.folders.length;
    const fileCount = tmpl.state.files.length + tmpl.state.forms.length + tmpl.state.documents.length;
    const total = folderCount + fileCount;

    if (total === 0) return null;

    const parts: string[] = [];
    if (folderCount) parts.push(`${folderCount} folder${folderCount === 1 ? "" : "s"}`);
    if (fileCount) parts.push(`${fileCount} file${fileCount === 1 ? "" : "s"}`);
    const countText = parts.join(" \u00B7 ") + " in template";

    return (
      <Box sx={{ px: "20px", py: "12px", borderTop: border(), bgcolor: palette.background.default }}>
        <Stack direction="row" alignItems="center" justifyContent="flex-start" gap="10px" sx={{ mb: "8px" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>{countText}</Typography>
          <Button
            variant="text"
            size="sm"
            onClick={tmpl.clearSelection}
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: "primary.light",
              textTransform: "none",
              minWidth: "auto",
              p: "2px 6px"
            }}
          >
            Clear all
          </Button>
        </Stack>
        <Stack direction="row" gap="6px" flexWrap="wrap">
          {templateChips.folders.map((chip) => (
            <Chip
              key={`folder-${chip.id}`}
              icon={<FolderOutlinedIcon sx={{ fontSize: 12, fill: "#071f35" }} />}
              label={truncateName(chip.name)}
              onDelete={() => tmpl.removeFolder(chip.id)}
              size="small"
              sx={{ maxWidth: 260, bgcolor: "transparent", border: "1px solid #7b92a9" }}
            />
          ))}
          {templateChips.files.map((chip) => (
            <Chip
              key={`file-${chip.id}`}
              icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 12, fill: "#071f35" }} />}
              label={truncateName(chip.name)}
              onDelete={() => tmpl.removeItem("files", chip.id)}
              size="small"
              sx={{ maxWidth: 260, bgcolor: "#dbe8f5" }}
            />
          ))}
          {templateChips.forms.map((chip) => (
            <Chip
              key={`form-${chip.id}`}
              icon={<DescriptionOutlinedIcon sx={{ fontSize: 12, fill: "#071f35" }} />}
              label={truncateName(chip.name)}
              onDelete={() => tmpl.removeItem("forms", chip.id)}
              size="small"
              sx={{ maxWidth: 260, bgcolor: "#dbe8f5" }}
            />
          ))}
          {templateChips.documents.map((chip) => (
            <Chip
              key={`doc-${chip.id}`}
              icon={<ArticleOutlinedIcon sx={{ fontSize: 12, fill: "#071f35" }} />}
              label={truncateName(chip.name)}
              onDelete={() => tmpl.removeItem("documents", chip.id)}
              size="small"
              sx={{ maxWidth: 260, bgcolor: "#dbe8f5" }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  // File mode
  const ext = selection as FilePickerSelectionReturn;
  const fileCount = ext.selectedCount;

  if (fileCount === 0) return null;

  return (
    <Box sx={{ mx: "20px", p: "12px", bgcolor: palette.background.default, borderRadius: "8px" }}>
      <Stack direction="row" alignItems="center" justifyContent="flex-start" gap="10px" sx={{ mb: "8px" }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>
          {fileCount} file{fileCount === 1 ? "" : "s"} selected
        </Typography>
        <Button
          variant="text"
          size="sm"
          onClick={ext.clearSelection}
          sx={{
            fontSize: 14,
            fontWeight: 400,
            color: "#26A6AE",
            textTransform: "none",
            minWidth: "auto",
            p: "2px 6px"
          }}
        >
          Clear all
        </Button>
      </Stack>
      <Stack direction="row" gap="6px" flexWrap="wrap">
        {fileChips.map((chip) => (
          <Chip
            key={chip.id}
            icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 12 }} />}
            label={truncateName(chip.name)}
            onDelete={() => ext.removeFile(chip.id)}
            size="small"
            sx={{ maxWidth: 260 }}
          />
        ))}
      </Stack>
    </Box>
  );
};
